import { useCallback, useRef } from "react"

import { toast } from "@coval/ui"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"

import {
  isPersistedTestSetId,
  mapDuplicateTestSetInputToTransport,
  useDuplicateTestSetTransport,
  useInvalidateTestSetTransportQueries,
} from "../../transport"

interface UseTestSetCloneActionParams {
  /** Runs once the copy exists and the list queries are stale, e.g. to navigate. */
  onCloned?: () => void
}

/**
 * Clones a test set and reports the outcome. Shared by the editor's header menu,
 * which then leaves for the list, and the list's own row menu, which stays.
 *
 * Refuses an unsaved test set and an empty one, and refuses to run twice at
 * once: the request is slow enough that a second click lands before the first
 * returns, and each one would produce a copy.
 */
export function useTestSetCloneAction({
  onCloned,
}: UseTestSetCloneActionParams = {}) {
  const { trackEvent } = useAnalytics()
  const duplicateTestSetMutation = useDuplicateTestSetTransport()
  const invalidateTestSetTransportQueries =
    useInvalidateTestSetTransportQueries()
  const isCloningRef = useRef(false)

  const cloneTestSet = useCallback(
    async ({
      testSetId,
      testCaseCount,
    }: {
      testSetId?: string
      /** Undefined when the caller has not loaded it yet, which blocks the clone. */
      testCaseCount?: number
    }): Promise<void> => {
      if (!testSetId) {
        return
      }
      if (!isPersistedTestSetId(testSetId)) {
        toast.danger({
          title: "Clone Failed",
          description: "Save the test set before cloning",
        })
        return
      }
      if (testCaseCount === undefined) {
        return
      }
      if (testCaseCount === 0) {
        toast.danger({
          title: "Clone Failed",
          description: "Cannot clone an empty test set",
        })
        return
      }
      if (isCloningRef.current) {
        return
      }
      isCloningRef.current = true

      try {
        await duplicateTestSetMutation.mutateAsync(
          mapDuplicateTestSetInputToTransport({ testSetId }),
        )
        await invalidateTestSetTransportQueries()
        trackEvent(TRACKING_EVENTS.TEST_SET.CLONED, { test_set_id: testSetId })
        toast.success({
          title: "Success",
          description: "Test set cloned successfully",
        })
        onCloned?.()
      } catch (error) {
        const description =
          error instanceof Error
            ? error.message
            : "Failed to clone test set. Please try again."
        trackEvent(TRACKING_EVENTS.TEST_SET.CLONE_FAILED, {
          error: description,
          testSetId,
        })
        toast.danger({ title: "Clone Failed", description })
      } finally {
        isCloningRef.current = false
      }
    },
    [
      duplicateTestSetMutation,
      invalidateTestSetTransportQueries,
      onCloned,
      trackEvent,
    ],
  )

  // `isCloning` has to come from here rather than from the caller's own
  // `useDuplicateTestSetTransport()`: a second observer of the same operation
  // carries its own pending state, so a caller reading its own would never see
  // this mutation run.
  return { cloneTestSet, isCloning: duplicateTestSetMutation.isPending }
}
